/**
 * Main Zustand Store
 *
 * Combines all slices and adds persistence middleware for configuration.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createConfigurationSlice, type ConfigurationSlice } from './configurationSlice';
import { createValidationSlice, type ValidationSlice } from './validationSlice';
import { createCalculationSlice, type CalculationSlice } from './calculationSlice';
import { createUISlice, type UISlice } from './uiSlice';

export type AppStore = ConfigurationSlice & ValidationSlice & CalculationSlice & UISlice;

export const useStore = create<AppStore>()(
  persist(
    (...a) => ({
      ...createConfigurationSlice(...a),
      ...createValidationSlice(...a),
      ...createCalculationSlice(...a),
      ...createUISlice(...a),
    }),
    {
      name: 'gpu-bom-calculator-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist only configuration and UI preferences
        configuration: state.configuration,
        theme: state.theme,
        showDerivations: state.showDerivations,
        sidebarOpen: state.sidebarOpen,
        expandedSections: Array.from(state.expandedSections), // Convert Set to Array for JSON
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.error('Failed to rehydrate state from localStorage:', error);
          // Clear corrupted storage
          try {
            localStorage.removeItem('gpu-bom-calculator-storage');
          } catch (e) {
            console.error('Failed to clear storage:', e);
          }
          return;
        }

        // Convert expandedSections back to Set
        if (state) {
          try {
            if (Array.isArray((state as any).expandedSections)) {
              state.expandedSections = new Set((state as any).expandedSections);
            } else {
              // Fallback to default expanded sections
              state.expandedSections = new Set(['compute', 'network-ew', 'network-ns', 'scale']);
            }
          } catch (e) {
            console.error('Error converting expandedSections:', e);
            state.expandedSections = new Set(['compute', 'network-ew', 'network-ns', 'scale']);
          }
        }
      },
    }
  )
);

// Selector hooks for optimized re-renders
export const useConfiguration = () => useStore((state) => state.configuration);
export const useValidation = () =>
  useStore((state) => ({
    result: state.validationResult,
    report: state.validationReport,
    isValidating: state.isValidating,
    hasErrors: state.hasErrors(),
    hasWarnings: state.hasWarnings(),
    errorCount: state.getErrorCount(),
    warningCount: state.getWarningCount(),
  }));

export const useCalculation = () =>
  useStore((state) => ({
    bom: state.bom,
    isCalculating: state.isCalculating,
    error: state.calculationError,
    lastCalculated: state.lastCalculated,
  }));

export const useUI = () =>
  useStore((state) => ({
    currentView: state.currentView,
    theme: state.theme,
    showDerivations: state.showDerivations,
    sidebarOpen: state.sidebarOpen,
    toasts: state.toasts,
  }));

// Action hooks
export const useConfigurationActions = () =>
  useStore((state) => ({
    setTotalGPUs: state.setTotalGPUs,
    setGPUsPerNode: state.setGPUsPerNode,
    updateConfiguration: state.updateConfiguration,
    updateGPUPlatform: state.updateGPUPlatform,
    updateServer: state.updateServer,
    updateNetwork: state.updateNetwork,
    updateRack: state.updateRack,
    updatePower: state.updatePower,
    resetConfiguration: state.resetConfiguration,
  }));

export const useValidationActions = () =>
  useStore((state) => ({
    setValidationResult: state.setValidationResult,
    setValidationReport: state.setValidationReport,
    setIsValidating: state.setIsValidating,
    clearValidation: state.clearValidation,
    getFieldErrors: state.getFieldErrors,
    getFieldWarnings: state.getFieldWarnings,
  }));

export const useCalculationActions = () =>
  useStore((state) => ({
    setBOM: state.setBOM,
    setIsCalculating: state.setIsCalculating,
    setCalculationError: state.setCalculationError,
    clearCalculation: state.clearCalculation,
    updateComputedValues: state.updateComputedValues,
  }));

export const useUIActions = () =>
  useStore((state) => ({
    setCurrentView: state.setCurrentView,
    toggleSection: state.toggleSection,
    expandSection: state.expandSection,
    collapseSection: state.collapseSection,
    setActiveTab: state.setActiveTab,
    addToast: state.addToast,
    removeToast: state.removeToast,
    toggleTheme: state.toggleTheme,
    toggleDerivations: state.toggleDerivations,
    openModal: state.openModal,
    closeModal: state.closeModal,
    setRightPanelContent: state.setRightPanelContent,
  }));
